// The API reference: quick start, authentication, every endpoint with a request and a real response, the objects,
// errors, limits and versioning, with a table of contents that follows the reader. Shown in the signed-in API tab
// (/api/docs) and on the public /developers/docs page. Content lives in apiDocsContent.js.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { highlight } from './codeHighlight';
import { snippets } from './apiModel';
import { ENDPOINTS, ERRORS, ERROR_EXAMPLE, LIMITS, OBJECTS, requestSnippet } from './apiDocsContent';
import './apiReference.css';

const LANGS = [['curl', 'curl'], ['python', 'Python'], ['javascript', 'JavaScript']];
const LANG_KEY = 'bumpups-api-docs-lang';

const GROUPS = ['Videos', 'Questions', 'Account'];
const TOC = [
  { title: 'Get started', items: [['overview', 'Overview'], ['quick-start', 'Quick start'], ['authentication', 'Authentication']] },
  ...GROUPS.map(group => ({ title: group, items: ENDPOINTS.filter(e => e.group === group).map(e => [e.id, e.title]) })),
  { title: 'Reference', items: [...OBJECTS.map(o => [o.id, o.title.replace(/^The /, '').replace(/^./, c => c.toUpperCase())]), ['errors', 'Errors'], ['limits', 'Limits'], ['versioning', 'Versioning']] },
];
const SECTION_IDS = TOC.flatMap(g => g.items.map(([id]) => id));

// One language choice for every sample on the page, remembered on this browser
function useLang() {
  const [lang, setLang] = useState(() => {
    try {
      const saved = window.localStorage.getItem(LANG_KEY);
      return LANGS.some(([id]) => id === saved) ? saved : 'curl';
    } catch {
      return 'curl';
    }
  });
  const choose = id => {
    setLang(id);
    try { window.localStorage.setItem(LANG_KEY, id); } catch { /* storage blocked */ }
  };
  return [lang, choose];
}

// The section being read, for the table of contents
function useActiveSection() {
  const [active, setActive] = useState(SECTION_IDS[0]);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-15% 0px -70% 0px' });
    SECTION_IDS.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return active;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked: the code is still selectable
    }
  };
  return (
    <button type="button" className="apid-copy" onClick={copy} aria-label="Copy code">
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function Code({ code, lang }) {
  const pieces = useMemo(() => highlight(code, lang), [code, lang]);
  return (
    <pre className="apid-pre">
      <code>
        {pieces.map((p, i) => (p.kind ? <span key={i} className={`apid-tok-${p.kind}`}>{p.text}</span> : p.text))}
      </code>
    </pre>
  );
}

// A code panel with a header: language tabs for requests, a status line for responses
function CodePanel({ title, code, lang, langs, onLang, status, note }) {
  return (
    <div className="apid-panel">
      <div className="apid-panel-head">
        <span className="apid-panel-title">{title}</span>
        {status && <span className={`apid-status apid-status--${String(status.code)[0]}xx`}>{status.code} {status.label}</span>}
        {langs && (
          <div className="apid-langs" role="tablist" aria-label="Language">
            {LANGS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={lang === id}
                className={`apid-lang${lang === id ? ' active' : ''}`} onClick={() => onLang(id)}>
                {label}
              </button>
            ))}
          </div>
        )}
        <CopyButton text={code} />
      </div>
      <Code code={code} lang={lang} />
      {note && <p className="apid-panel-note">{note}</p>}
    </div>
  );
}

const Method = ({ method }) => <span className={`apid-method apid-method--${method.toLowerCase()}`}>{method}</span>;

function FieldTable({ title, rows }) {
  return (
    <div className="apid-fields">
      <p className="apid-fields-title">{title}</p>
      <dl>
        {rows.map(([name, type, required, text]) => (
          <div key={name} className="apid-field">
            <dt>
              <code>{name}</code>
              <span className="apid-field-type">{type}</span>
              {required === true && <span className="apid-field-req">required</span>}
            </dt>
            <dd>{text}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Endpoint({ endpoint, baseUrl, lang, onLang }) {
  const { response } = endpoint;
  return (
    <section id={endpoint.id} className="apid-section apid-endpoint">
      <h3 className="apid-h3">{endpoint.title}</h3>
      <p className="apid-route"><Method method={endpoint.method} /><code>/v1{endpoint.path}</code></p>
      <p className="apid-text">{endpoint.summary}</p>
      {endpoint.params && <FieldTable title="Path parameters" rows={endpoint.params} />}
      {endpoint.headers && <FieldTable title="Headers" rows={endpoint.headers} />}
      {endpoint.body && <FieldTable title="Body (JSON)" rows={endpoint.body} />}
      {endpoint.notes && <p className="apid-text apid-note">{endpoint.notes}</p>}
      <div className="apid-examples">
        <CodePanel title="Request" code={requestSnippet(lang, endpoint, baseUrl)} lang={lang} langs onLang={onLang} />
        <CodePanel
          title="Response"
          status={{ code: response.status, label: response.label }}
          code={(response.headers ? `${response.headers}\n\n` : '') + JSON.stringify(response.body, null, 2)}
          lang={response.headers ? 'plain' : 'json'}
          note={response.trimmed}
        />
      </div>
    </section>
  );
}

export default function ApiReference({ baseUrl, keysLink }) {
  const [lang, setLang] = useLang();
  const active = useActiveSection();

  const jump = (e, id) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    try { window.history.replaceState(null, '', `#${id}`); } catch { /* ignore */ }
  };

  // Opening a link to a section (e.g. /developers/docs#errors) lands on it
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id && SECTION_IDS.includes(id)) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 50);
  }, []);

  return (
    <div className="apid">
      <nav className="apid-toc" aria-label="On this page">
        {TOC.map(group => (
          <div key={group.title} className="apid-toc-group">
            <p className="apid-toc-title">{group.title}</p>
            {group.items.map(([id, label]) => (
              <a key={id} href={`#${id}`} onClick={e => jump(e, id)} className={`apid-toc-link${active === id ? ' active' : ''}`}>
                {label}
              </a>
            ))}
          </div>
        ))}
      </nav>

      <div className="apid-body">
        <section id="overview" className="apid-section">
          <h2 className="apid-h2">Overview</h2>
          <p className="apid-text">
            The Bumpups API turns YouTube links into timestamped chapters and answers questions about the videos. It
            comes with the Pro plan and shares your minutes and chat limits with the web app. Every video you process
            also appears in your Workspace.
          </p>
          <div className="apid-base">
            <span className="apid-base-label">Base URL</span>
            <code>{baseUrl}</code>
            <CopyButton text={baseUrl} />
          </div>
          <div className="apid-actions">
            {keysLink && <Link to={keysLink.to} className="apid-btn apid-btn--primary">{keysLink.label}</Link>}
            <a href="/openapi.json" download className="apid-btn">Download OpenAPI 3.1</a>
          </div>
        </section>

        <section id="quick-start" className="apid-section">
          <h2 className="apid-h2">Quick start</h2>
          <ol className="apid-steps">
            <li><strong>Create a key</strong> on the API page of the Bumpups app and keep it on your server.</li>
            <li><strong>Send a YouTube link</strong> to <code>POST /v1/videos</code>. You get the video back right away with status <code>processing</code>.</li>
            <li><strong>Check back</strong> at <code>GET /v1/videos/{'{id}'}</code> until the status is <code>ready</code>, then use the chapters or ask questions.</li>
          </ol>
          <CodePanel title="Process a video and wait for its chapters" code={snippets(baseUrl)[lang]} lang={lang} langs onLang={setLang} />
        </section>

        <section id="authentication" className="apid-section">
          <h2 className="apid-h2">Authentication</h2>
          <p className="apid-text">
            Send your key in the <code>Authorization</code> header of every request, over HTTPS. Keys start
            with <code>bu_live_</code>. Each one is shown once, when you create it. Create up to 5, and revoke any of
            them from the API page: a revoked key stops working within a minute.
          </p>
          <CodePanel title="Header" code="Authorization: Bearer bu_live_YOUR_KEY" lang="plain" />
          <p className="apid-text apid-note">
            Keep keys out of web pages and apps: the API doesn&apos;t accept calls from browsers.
          </p>
        </section>

        {GROUPS.map(group => (
          <div key={group} className="apid-group">
            <h2 className="apid-h2">{group}</h2>
            {ENDPOINTS.filter(e => e.group === group).map(e => (
              <Endpoint key={e.id} endpoint={e} baseUrl={baseUrl} lang={lang} onLang={setLang} />
            ))}
          </div>
        ))}

        {OBJECTS.map(object => (
          <section key={object.id} id={object.id} className="apid-section">
            <h2 className="apid-h2">{object.title}</h2>
            <FieldTable title="Fields" rows={object.fields.map(([name, type, text]) => [name, type, null, text])} />
          </section>
        ))}

        <section id="errors" className="apid-section">
          <h2 className="apid-h2">Errors</h2>
          <p className="apid-text">
            Errors use the RFC 9457 problem format (<code>application/problem+json</code>). Branch on <code>code</code>,
            show <code>detail</code> to people, and quote <code>request_id</code> (also in the <code>X-Request-Id</code> header)
            if you contact support.
          </p>
          <CodePanel title="Example" status={{ code: 402, label: 'Payment Required' }} code={JSON.stringify(ERROR_EXAMPLE, null, 2)} lang="json" />
          <div className="apid-errors">
            {ERRORS.map(([status, codes, text]) => (
              <div key={status} className="apid-error">
                <span className={`apid-status apid-status--${String(status)[0]}xx`}>{status}</span>
                <div>
                  <p className="apid-error-codes">{codes.map(c => <code key={c} id={c}>{c}</code>)}</p>
                  <p className="apid-error-text">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="limits" className="apid-section">
          <h2 className="apid-h2">Limits</h2>
          <dl className="apid-limits">
            {LIMITS.map(([name, text]) => (
              <div key={name} className="apid-limit"><dt>{name}</dt><dd>{text}</dd></div>
            ))}
          </dl>
        </section>

        <section id="versioning" className="apid-section">
          <h2 className="apid-h2">Versioning</h2>
          <p className="apid-text">
            The version is in the path. New fields, endpoints and enum values can appear in v1 at any time, so ignore
            what you don&apos;t recognize. A breaking change would come as /v2, with the old version kept running for at
            least 12 months.
          </p>
        </section>
      </div>
    </div>
  );
}
